/**
 * Shared QueryClient instance.
 *
 * Extracted from src/main.tsx so non-React modules (axios interceptors,
 * cross-tab logout listener, mutation invalidators) can call
 * queryClient.clear() / .invalidateQueries() without prop-drilling.
 *
 * The defaults below are ratified by ADR-0008
 * (docs/adr/0008-query-client-defaults-and-data-freshness.md): the global
 * `staleTime: 0` mount-revalidate + invalidate model stays; screens that
 * provably tolerate staleness opt in to a class from
 * `@/shared/api` `FRESHNESS` (freshness.ts) per query.
 */

import { QueryClient, MutationCache, QueryCache } from '@tanstack/react-query'
import { Toast } from '@/shared/ui/organisms/toast'
import { ApiError } from './api-error'

/**
 * Shared suppression policy for BOTH global error toasts (query + mutation)
 * — keep the two caches in lockstep:
 * - `meta: { skipGlobalError: true }` per-query/per-mutation opt-out.
 * - Offline: NetworkProvider already shows a persistent "you are offline"
 *   warning; one toast per failed request is noise.
 * - AbortSignal cancellation (unmount / queryKey change) is not a
 *   user-facing failure. TQ usually swallows its own CancelledError before
 *   reaching here, but an axios abort surfacing through toApiError must not
 *   toast either.
 */
function suppressGlobalToast(error: unknown, meta: Record<string, unknown> | undefined): boolean {
  if (meta?.skipGlobalError) return true
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  if (error instanceof ApiError && error.kind === 'cancelled') return true
  return false
}

export const queryClient = new QueryClient({
  /**
   * Auto-invalidate list queries when a mutation with a matching
   * `mutationKey` prefix succeeds. The mutation declares the prefix; any
   * query whose first key element starts with that prefix is invalidated.
   */
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      const prefix = mutation.options.mutationKey?.[0]
      if (typeof prefix !== 'string') return
      queryClient.invalidateQueries({
        predicate: (query) => {
          const qk = query.queryKey[0]
          return typeof qk === 'string' && qk.startsWith(prefix)
        },
      })
    },
    /**
     * Global mutation-error toast — mirror of `QueryCache.onError` below
     * (error-toast-dedup MR, audit/09-crosscutting.md §2). Suppression policy
     * shared via `suppressGlobalToast` (meta opt-out + offline + cancelled).
     *
     * Additionally skipped when the mutation declares a HOOK-LEVEL `onError`
     * (`useMutation({ onError })`) — that handler owns the error UX (e.g.
     * accounting optimistic rollbacks toast AFTER restoring snapshots).
     * mutate()-level callbacks are INVISIBLE here — MutationObserver keeps
     * them in a private #mutateOptions field (verified @tanstack/query-core
     * 5.x mutationObserver.js) — so call sites whose `mutate(_, { onError })`
     * handler still toasts must opt out via `meta: { skipGlobalError: true }`.
     */
    onError: (error, _variables, _context, mutation) => {
      if (typeof mutation.options.onError === 'function') return
      if (suppressGlobalToast(error, mutation.meta)) return
      Toast({ type: 'error', message: error?.message ?? 'Unknown error' })
    },
  }),
  /**
   * Per-query opt-out: useQuery({ meta: { skipGlobalError: true } }).
   * Suppression policy (offline, cancelled, meta) documented on
   * `suppressGlobalToast` above.
   */
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (suppressGlobalToast(error, query.meta)) return
      // `request()` rejects with a normalized `ApiError` (shared/api/api-error.ts)
      // since FIX_BACKLOG #9; `message` carries the envelope message. The ??
      // guard covers empty-message edge cases and non-ApiError throwers.
      Toast({ type: 'error', message: error?.message ?? 'Unknown error' })
    },
  }),
  defaultOptions: {
    queries: {
      retry: 0,
      staleTime: 0,
      gcTime: 10 * 60 * 1000,
      refetchOnReconnect: 'always',
    },
  },
})
